import React, { useState } from 'react';
import SourceCitation from './SourceCitation';
import operatorService from '../../services/operatorService';

const OperatorResponse = ({ query, currentUser }) => {
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSaveToVault = async () => {
    if (!currentUser || saving || saved) return;

    setSaving(true);
    try {
      const vaultData = {
        user_id: currentUser.id || 'demo-user',
        company_id: currentUser.company_id || 'demo-company',
        query: query.text,
        answer: query.response.answer,
        sources: query.response.sources || [],
        frameworks: query.response.frameworks || [],
        confidence: query.response.confidence || 'MEDIUM',
        tags: [],
        notes: ''
      };

      await operatorService.saveToVault(vaultData);
      setSaved(true);
    } catch (error) {
      console.error('Failed to save to vault:', error);
      alert('Failed to save to vault. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPDF = () => {
    try {
      // Create PDF content as HTML
      const pdfContent = generatePDFHTML(query);

      // Create a blob with the HTML content
      const blob = new Blob([pdfContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);

      // Create temporary link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = `ORACLE_Response_${new Date().toISOString().slice(0,10)}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download PDF:', error);
      alert('Failed to download. Please try again.');
    }
  };

  const handleCopy = async () => {
    try {
      const textToCopy = `Question: ${query.text}\n\nAnswer: ${query.response.answer}\n\nConfidence: ${query.response.confidence}\n\nSources:\n${query.response.sources?.map((s, i) => `${i + 1}. ${s.title} (${s.framework})`).join('\n') || 'No sources'}`;
      await navigator.clipboard.writeText(textToCopy);
      alert('Copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy:', error);
      alert('Failed to copy. Please try again.');
    }
  };

  const handleShare = () => {
    // Placeholder for share functionality
    alert('Share functionality coming soon!');
  };

  const generatePDFHTML = (query) => {
    const date = new Date().toLocaleString();
    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>ORACLE Response - ${new Date().toISOString().slice(0,10)}</title>
  <style>
    body { font-family: 'IBM Plex Sans', 'Segoe UI', sans-serif; max-width: 800px; margin: 40px auto; padding: 20px; color: #333; }
    .header { border-bottom: 3px solid #4d8ef0; padding-bottom: 20px; margin-bottom: 30px; }
    .logo { font-family: 'IBM Plex Mono', monospace; font-size: 24px; font-weight: 700; color: #4d8ef0; }
    .subtitle { color: #666; font-size: 12px; margin-top: 5px; }
    .section { margin: 30px 0; }
    .section-title { font-family: 'IBM Plex Mono', monospace; font-size: 11px; font-weight: 700; color: #666; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 15px; }
    .question { background: #f0f9ff; border-left: 4px solid #4d8ef0; padding: 15px 20px; font-size: 15px; font-weight: 500; }
    .answer { line-height: 1.8; font-size: 14px; white-space: pre-wrap; }
    .confidence { display: inline-block; padding: 4px 12px; border-radius: 3px; font-size: 11px; font-weight: 700; margin: 10px 0; }
    .confidence-high { background: #e6f7ed; color: #107c10; border: 1px solid #107c10; }
    .confidence-medium { background: #fff4e5; color: #ca5010; border: 1px solid #ca5010; }
    .confidence-low { background: #fef0f1; color: #a4262c; border: 1px solid: #a4262c; }
    .sources { margin-top: 20px; }
    .source { background: #fafafa; border: 1px solid #e0e0e0; border-radius: 4px; padding: 12px 15px; margin: 10px 0; }
    .source-title { font-weight: 600; color: #333; margin-bottom: 5px; }
    .source-meta { font-size: 11px; color: #666; }
    .footer { margin-top: 50px; padding-top: 20px; border-top: 1px solid #e0e0e0; font-size: 11px; color: #999; text-align: center; }
  </style>
</head>
<body>
  <div class="header">
    <div class="logo">TRANSTAR COMPLIANCE TERMINAL</div>
    <div class="subtitle">ORACLE Regulatory Intelligence</div>
  </div>

  <div class="section">
    <div class="section-title">Query</div>
    <div class="question">${escapeHTML(query.text)}</div>
  </div>

  <div class="section">
    <div class="section-title">Response</div>
    <div class="confidence confidence-${query.response.confidence?.toLowerCase() || 'medium'}">${query.response.confidence || 'MEDIUM'} CONFIDENCE</div>
    <div class="answer">${escapeHTML(query.response.answer)}</div>
  </div>

  ${query.response.sources && query.response.sources.length > 0 ? `
  <div class="section sources">
    <div class="section-title">Source Documents (${query.response.sources.length})</div>
    ${query.response.sources.map((source, idx) => `
      <div class="source">
        <div class="source-title">${idx + 1}. ${escapeHTML(source.title)}</div>
        <div class="source-meta">
          Framework: ${source.framework || 'N/A'} |
          Relevance: ${source.relevance_score || 'N/A'}% |
          Source: ${source.source || 'N/A'}
        </div>
      </div>
    `).join('')}
  </div>
  ` : ''}

  <div class="footer">
    Generated on ${date} | Model: ${query.response.model || 'Gemini 2.5 Flash'} | Response Time: ${query.latency || 'N/A'}s<br>
    <strong>Disclaimer:</strong> This is regulatory information only and does not constitute legal advice.
  </div>
</body>
</html>`;
  };

  const escapeHTML = (str) => {
    if (!str) return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  if (query.status === 'processing') {
    return (
      <div className="msg-oracle-ultra">
        <div className="msg-oracle-header-ultra">
          <strong className="oracle-name-ultra">ORACLE</strong>
          · Processing your request...
        </div>
        <div className="msg-oracle-card-ultra processing">
          <div className="msg-body-ultra">
            Searching {query.frameworks?.length > 0 ? query.frameworks.join(', ') + ' ' : ''}regulatory documents and generating response...
          </div>
        </div>
      </div>
    );
  }

  if (query.status === 'error') {
    return (
      <div className="msg-oracle-ultra">
        <div className="msg-oracle-header-ultra">
          <strong className="oracle-name-ultra">ORACLE</strong>
          · Error
        </div>
        <div className="msg-oracle-card-ultra error">
          <div className="msg-body-ultra error-text">
            {query.error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="msg-oracle-ultra">
      <div className="msg-oracle-header-ultra">
        <strong className="oracle-name-ultra">ORACLE</strong>
        · LLM 2.5 Flash · RAG Mode · {query.latency}s
      </div>
      <div className="msg-oracle-card-ultra">
        <div className="msg-body-ultra">
          {query.response.answer}
        </div>

        {query.response.sources && query.response.sources.length > 0 && (
          <div className="sources-section-ultra">
            <div className="sources-header-ultra">Source Documents — {query.response.sources.length} retrieved</div>
            <div className="sources-list-ultra">
              {query.response.sources.map((source, idx) => (
                <SourceCitation
                  key={idx}
                  index={idx + 1}
                  source={source}
                />
              ))}
            </div>
          </div>
        )}

        <div className="msg-actions-ultra">
          <button className="action-btn-ultra" onClick={handleDownloadPDF}>Download PDF</button>
          <button className="action-btn-ultra" onClick={handleCopy}>Copy</button>
          <button className="action-btn-ultra" onClick={handleShare}>Share</button>
          {currentUser && (
            <button
              className={`action-btn-ultra primary ${saved ? 'saved' : ''}`}
              onClick={handleSaveToVault}
              disabled={saving || saved}
            >
              {saved ? 'Saved to Vault' : saving ? 'Saving...' : 'Save to Vault'}
            </button>
          )}
          <div className="msg-meta-ultra">Regulatory information only — Not legal advice</div>
        </div>
      </div>
    </div>
  );
};

export default OperatorResponse;
